import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import AuthRoutes from "@/modules/Auth/routes";
import DashboardRoutes from "@/modules/Dashboard/routes";
import ProfileRoutes from "@/modules/Profile/routes";
import PagesRoutes from "@/modules/Pages/routes";
import WalletRoutes from "@/modules/Wallet/routes";
import ExchangesRoutes from "@/modules/Exchanges/routes";
import BotsRoutes from "@/modules/Bots/routes";
import GuidesRoutes from "@/modules/Guide/routes";
import PlanRoutes from "@/modules/Plan/routes";
import MobileRoutes from "@/modules/Mobile/routes";
import NotificationRoutes from "@/modules/Notification/routes";
import { useAuthStore } from "@/modules/Auth/stores";
import { computed } from "vue";
const { showToast } = useCustomToast();
import { JwtService } from "@/services/JwtService";
import { useCustomToast } from "@/composables/useCustomToast";
import LandingRoutes from "@/modules/Landing/routes";
import i18n from "@/plugins/i18n";

// Available locales
const availableLocales = ["en", "ru", "uz", "ar"];
const defaultLocale = "en";

// Get stored locale or default
const storedLocale = localStorage.getItem("locale") || defaultLocale;

// Generate localized routes
const generateLocalizedRoutes = (
  routes: Array<RouteRecordRaw>,
  locale: string,
): Array<RouteRecordRaw> => {
  return routes.map((route) => {
    // Clone the route to avoid mutating the original
    const localizedRoute = { ...route };

    // Add locale prefix to path
    localizedRoute.path = `/${locale}${route.path}`;

    // Update name with locale prefix if name exists
    if (localizedRoute.name) {
      localizedRoute.name = `${locale}-${localizedRoute.name.toString()}`;
    }

    // If there are children routes, localize them too
    if (localizedRoute.children && localizedRoute.children.length > 0) {
      localizedRoute.children = generateLocalizedRoutes(
        localizedRoute.children,
        locale,
      );
    }

    return localizedRoute;
  });
};

// Base routes without locale prefix
const baseRoutes: Array<RouteRecordRaw> = [
  ...AuthRoutes,
  ...DashboardRoutes,
  ...ProfileRoutes,
  ...PagesRoutes,
  ...WalletRoutes,
  ...ExchangesRoutes,
  ...BotsRoutes,
  ...GuidesRoutes,
  ...PlanRoutes,
  ...MobileRoutes,
  ...NotificationRoutes,
  ...LandingRoutes,
];

// Generate localized versions of all routes
let localizedRoutes: Array<RouteRecordRaw> = [];
availableLocales.forEach((locale) => {
  localizedRoutes = [
    ...localizedRoutes,
    ...generateLocalizedRoutes(baseRoutes, locale),
  ];
});

// Create final routes array with localized routes and special routes
const routes: Array<RouteRecordRaw> = [
  // Root redirect to preferred locale
  {
    path: "/",
    redirect: () => {
      return `/${storedLocale}`;
    },
  },

  // Locale root redirects to respective dashboard/home pages
  ...availableLocales.map((locale) => ({
    path: `/${locale}`,
    redirect: `/${locale}/dashboard`,
  })),

  // Add all localized routes
  ...localizedRoutes,
  {
    path: "/sitemap.xml",
    name: "sitemap",
    beforeEnter: () => {
      window.location.href = "/sitemap.xml";
      return false;
    },
    component: { render: () => null }, // Add this line
  },
  // Special routes
  {
    path: "/:pathMatch(.*)*",
    name: "404",
    meta: {
      layout: "error",
    },
    component: () => import("@/layout/Error/LError.vue"),
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    // always scroll to top
    return { top: 0, behavior: "smooth" };
  },
});

router.onError((error, to) => {
  if (
    error.message.includes("Failed to fetch dynamically imported module") ||
    error.message.includes("Importing a module script failed")
  ) {
    if (!to?.fullPath) {
      window.location.reload();
    } else {
      window.location.href = to.fullPath;
    }
  }
});

router.beforeEach(async (to) => {
  // Skip locale handling for sitemap.xml
  if (to.path === "/sitemap.xml") {
    return true;
  }

  // Extract locale from path
  const locale = to.path.split("/")[1];
  if (to.path == "/dashboard" || to.path == `/${locale}/dashboard`) {
    return { path: `/${locale}/home` };
  }
  // Handle 404 and other error pages
  if (["404", "403", "500"].includes((to.name || "").toString())) {
    return true;
  }

  // If no locale in path or invalid locale, redirect to preferred locale
  if (!locale || !availableLocales.includes(locale)) {
    return { path: `/${storedLocale}${to.fullPath}` };
  }

  if (i18n.global.locale.value !== locale) {
    // Update i18n locale if different
    i18n.global.locale.value = locale as "en" | "ru" | "uz";
    localStorage.setItem("locale", locale);
  }

  // Redirect specific routes that are no longer valid
  const pathWithoutLocale = to.path.substring(locale.length + 1);
  if (
    pathWithoutLocale === "/bots/add" ||
    pathWithoutLocale.includes("/bots/edit") ||
    pathWithoutLocale === "/dashboard"
  ) {
    return { path: `/${locale}/home` };
  }

  const authStore = useAuthStore();
  if (to?.query?.verification_token) {
    authStore
      .userEmailVerify(String(to.query?.verification_token))
      .then((res) => {
        if (res) {
          authStore.fetchUserData();
          showToast(
            locale === "ru"
              ? "Аккаунт успешно подтвержден"
              : locale === "uz"
                ? "Hisob muvaffaqiyatli tasdiqlandi"
                : "Account successfully verified",
            "success",
          );
        }
      })
      .catch((err) => {
        showToast(err?.response?.data?.non_field_errors?.[0], "error");
      })
      .finally(() => {
        setTimeout(() => {
          router.push({ name: to.name, query: {} });
        }, 100);
      });
  }

  const user = computed(() => authStore.user);
  if (JwtService.getToken() && !Object.keys(user.value)?.length) {
    await authStore.fetchUserData().catch((err) => {
      if (err?.status === 401) {
        authStore.logout();
      }
    });
  }

  // Handle auth redirects with locale
  const pathSegments = to.path.split("/");
  const isAuthRoute = pathSegments.length > 2 && pathSegments[2] === "auth";

  if (Object.keys(user.value)?.length && isAuthRoute) {
    return { path: `/${locale}` };
  }

  if (to.name?.toString().includes("StaticPage")) return true;
  if (to.name?.toString().includes("Mobile")) return true;
  if (to.name?.toString().includes("Static")) return true;
  if (to.name?.toString().includes("ResetPassword")) return true;
  if (to.name?.toString()?.includes("Landing")) return true;

  if (
    !isAuthRoute &&
    !JwtService.getToken() &&
    !Object.keys(user.value)?.length
  ) {
    return { path: `/${locale}/home` };
  } else {
    return true;
  }
});

export default router;
